/**
 * Detect the user's country from browser geolocation (GPS).
 * Prefers cached coords in localStorage.userLocation when available.
 */

async function reverseGeocodeCountry(latitude, longitude) {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=0&addressdetails=1`,
    {
      headers: {
        Accept: "application/json",
      },
    }
  );
  if (!response.ok) return null;
  const data = await response.json();
  const country = data?.address?.country;
  return typeof country === "string" && country.trim() ? country.trim() : null;
}

function readCachedCoords() {
  try {
    const raw = localStorage.getItem("userLocation");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat ?? parsed?.latitude);
    const long = Number(parsed?.long ?? parsed?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(long)) return null;
    return { lat, long };
  } catch {
    return null;
  }
}

function requestBrowserCoords(timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const long = position.coords.longitude;
        try {
          localStorage.setItem("userLocation", JSON.stringify({ lat, long }));
        } catch {
          /* ignore quota */
        }
        resolve({ lat, long });
      },
      () => resolve(null),
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 10 * 60 * 1000 }
    );
  });
}

/** Returns country name (e.g. "Rwanda") or null if detection fails. */
export async function detectUserCountry() {
  const coords = readCachedCoords() ?? (await requestBrowserCoords());
  if (!coords) return null;
  try {
    return await reverseGeocodeCountry(coords.lat, coords.long);
  } catch {
    return null;
  }
}

export async function detectUserLocation() {
  const coords = readCachedCoords() ?? (await requestBrowserCoords());
  if (!coords) return { lat: null, long: null, country: null };
  let country = null;
  try {
    country = await reverseGeocodeCountry(coords.lat, coords.long);
  } catch {
    country = null;
  }
  return { ...coords, country };
}
